import {
  Row,
  Cell,
} from '@teetsh/app/src/containers/EmploiDuTemps/Print/types';

interface GridCell {
  eventId?: string;
}

type GridTable = GridCell[][];

export function createGrid(rows: Row[], numberOfColumns: number): GridTable {
  const grid: GridTable = [];

  // Initialize grid with empty rows based on the input rows
  for (let i = 0; i < rows.length; i++) {
    grid[i] = Array(numberOfColumns).fill({});
  }

  // Fill the grid with events
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];
    let colIndex = 0;

    for (const cell of row.cells) {
      // Skip already filled positions
      while (grid[rowIndex][colIndex]?.eventId) {
        colIndex++;
      }

      if (cell.event) {
        // Fill all cells that this event spans
        for (let r = 0; r < cell.rowspan; r++) {
          for (let c = 0; c < cell.colspan; c++) {
            if (rowIndex + r < grid.length && colIndex + c < numberOfColumns) {
              grid[rowIndex + r][colIndex + c] = {
                eventId: cell.event.id,
              };
            }
          }
        }
      }
      colIndex += cell.colspan;
    }
  }

  return grid;
}

/**
 * Finds the grid column position of a cell by its event ID
 */
function findEventPositionInGrid(
  grid: GridTable,
  rowIndex: number,
  eventId: string,
): number {
  return grid[rowIndex].findIndex(cell => cell.eventId === eventId);
}

/**
 * Finds the first empty grid position after a given starting position
 */
function findFirstEmptyPosition(
  grid: GridTable,
  rowIndex: number,
  startPos: number,
  numberOfColumns: number,
): number {
  for (let p = startPos; p < numberOfColumns; p++) {
    if (!grid[rowIndex][p]?.eventId) {
      return p;
    }
  }
  return -1;
}

/**
 * Checks if a range of grid positions is empty across multiple rows
 * Used to verify if we can safely merge cells without overlapping events
 */
function isGridRangeEmpty(
  grid: GridTable,
  startRow: number,
  startCol: number,
  rowCount: number,
  colCount: number,
  maxCols: number,
): boolean {
  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < colCount; c++) {
      if (
        startRow + r < grid.length &&
        startCol + c < maxCols &&
        grid[startRow + r][startCol + c]?.eventId
      ) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Determines if two cells can be merged based on grid positions
 * A merge is possible when:
 * - The next cell is empty (no event)
 * - All grid positions the merged cell would occupy are available
 */
function canMergeCells(
  grid: GridTable,
  rowIndex: number,
  currentCell: Cell,
  nextCell: Cell,
  currentGridPos: number,
  numberOfColumns: number,
): boolean {
  if (nextCell.event) {
    return false;
  }

  // Find where the empty cell actually starts in the grid
  const currentEndPos = currentGridPos + currentCell.colspan;
  const emptyStartPos = findFirstEmptyPosition(
    grid,
    rowIndex,
    currentEndPos,
    numberOfColumns,
  );

  if (emptyStartPos === -1) {
    return false;
  }

  // IMPORTANT: The empty cell must be immediately adjacent to the current cell
  // If there's a gap between the current cell and the first empty position,
  // we should not merge because the nextCell in the cells array doesn't
  // actually correspond to that empty position in the grid
  if (emptyStartPos !== currentEndPos) {
    return false;
  }

  // Check if all positions for the empty cell are actually empty
  // for all rows spanned by the current cell
  return isGridRangeEmpty(
    grid,
    rowIndex,
    emptyStartPos,
    currentCell.rowspan,
    nextCell.colspan,
    numberOfColumns,
  );
}

/**
 * Adjusts empty cells in rows below when a merge happens
 * When cells with rowspan > 1 are merged, we need to adjust or remove
 * corresponding empty cells in the rows they span
 *
 * Note: This function mutates the rows array directly rather than being pure.
 * A pure approach would require tracking modified rows separately and managing
 * complex state updates across multiple row indices, making the code harder to
 * understand. Since we're already iterating through rows and need to modify
 * multiple rows based on rowspan relationships, direct mutation is simpler.
 */
function adjustEmptyCellsBelow(
  rows: Row[],
  rowIndex: number,
  currentCell: Cell,
  mergedCellColspan: number,
): void {
  for (let r = 1; r < currentCell.rowspan; r++) {
    if (rowIndex + r >= rows.length) {
      continue;
    }

    const belowCells = rows[rowIndex + r].cells;

    // Find the first empty cell and adjust it
    for (let cellIdx = 0; cellIdx < belowCells.length; cellIdx++) {
      const cell = belowCells[cellIdx];
      if (!cell.event) {
        if (cell.colspan > mergedCellColspan) {
          // Reduce its colspan
          cell.colspan -= mergedCellColspan;
        } else {
          // Remove it entirely
          belowCells.splice(cellIdx, 1);
        }
        break; // Only adjust the first empty cell found
      }
    }
  }
}

/**
 * Optimizes table layout by merging adjacent empty cells to reduce visual clutter.
 * When rendering a timetable, empty cells (those without events) can create unnecessary
 * visual separation. This function merges these empty cells with their non-empty
 * neighbors by combining their colspans, resulting in a cleaner and more readable
 * table layout. Only cells with rowspan of 1 are considered for merging to preserve
 * the table's vertical structure
 *
 * We could extend this to hande rowspan > 1
 * there are also an edge case the there are more that 2 non empty cells in a row
 * with a rowspan of 1, they should grow equally, but for now, only the last non empty
 * will grow
 */
export function compactRows(rows: Row[], numberOfColumns: number): Row[] {
  // Create grid representation to help determine cell relationships
  const grid = createGrid(rows, numberOfColumns);

  return rows.map((row, rowIndex) => {
    const cells = [...row.cells];

    for (let i = 0; i < cells.length - 1; i++) {
      const currentCell = cells[i];
      const nextCell = cells[i + 1];

      // Skip empty cells - we only merge FROM cells with events
      if (!currentCell.event) {
        continue;
      }

      // Find the current cell's position in the grid
      const currentGridPos = findEventPositionInGrid(
        grid,
        rowIndex,
        currentCell.event.id,
      );

      // Check if we can merge with the next cell
      if (
        canMergeCells(
          grid,
          rowIndex,
          currentCell,
          nextCell,
          currentGridPos,
          numberOfColumns,
        )
      ) {
        // Perform the merge
        currentCell.colspan += nextCell.colspan;
        cells.splice(i + 1, 1);
        i--; // Adjust index since we removed an element

        // When a cell with rowspan > 1 is merged with an adjacent empty cell,
        // it expands horizontally across ALL the rows it spans.
        // Example: If Event A (rowspan=3) merges with an empty cell in row 0,
        // Event A now occupies that extra column in rows 0, 1, and 2.
        // We must remove/reduce the corresponding empty cells in rows 1 and 2
        // to prevent them from overlapping with Event A's expanded area.
        if (currentCell.rowspan > 1) {
          adjustEmptyCellsBelow(rows, rowIndex, currentCell, nextCell.colspan);
        }
      }
    }

    return {
      ...row,
      cells,
    };
  });
}
